import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * The Free Wall — every free product, service, AI-agent, and resource
 * the family offers.
 *
 * Founder ruling 2026-08-11:
 *   "New free Sage product: WhistleBlower. Place this in Free Products Tab
 *   at the very bottom, and give it a smaller (1/4 size) spot next to the
 *   free products bolded and obvious, draw attention to it."
 *
 * Layout:
 *   - Regular free items in a 2-col grid at top
 *   - WhistleBlower spotlighted separately at the bottom (smaller, loud)
 */
export default function FreeWallPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/sage/free-wall`).then((r) => r.json()).then(setD).catch(() => setD(null));
  }, []);

  const items = d?.items || [];
  const whistleblower = items.find((i) => i.key === "whistleblower");
  const regular = items.filter((i) => i.key !== "whistleblower");

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The Free Wall</div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4">
          Free. All of it.
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-10 sage-prose">
          {d?.note || "Every one of these is free. No paywall. No credit card. No account for most."}
        </p>

        {/* ── ASK-BOX · anonymous FAQ · cache-first · Founder ruling 2026-08-11 ─── */}
        <FreeWallFAQAskBox />

        {/* ── SEARCH · live keyword filter across free items ─── */}
        <FreeWallSearch items={regular} whistleblower={whistleblower} />
      </div>
    </div>
  );
}

function FreeWallSearch({ items, whistleblower }) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = !needle ? items : items.filter((it) => {
    const hay = `${it.name || ""} ${it.tagline || ""} ${it.for_whom || ""} ${it.key || ""}`.toLowerCase();
    return hay.includes(needle);
  });
  return (
    <>
      <div className="mb-6 flex items-center gap-3" data-testid="free-wall-search-wrap">
        <input
          data-testid="free-wall-search-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter the free wall · try 'legal' · 'refuge' · 'health'"
          className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
          maxLength={80}
        />
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted whitespace-nowrap" data-testid="free-wall-search-count">
          {filtered.length} shown
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="free-wall-grid">
        {filtered.map((item) => (
          <FreeCard key={item.key} item={item} />
        ))}
      </div>

      {/* ── WhistleBlower · bottom spotlight · 1/4-size hero ─── */}
      {whistleblower && !needle && (
        <div className="mt-16" data-testid="whistleblower-spotlight-wrap">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-3">
            And one more thing — the loudest free door we've built
          </div>
          <Link
            to={whistleblower.route}
            data-testid="whistleblower-spotlight"
            className="group grid md:grid-cols-4 gap-0 border-4 border-sage-accent bg-sage-text text-sage-bg hover:bg-sage-accent transition-colors duration-200 overflow-hidden"
          >
            <div className="p-6 bg-sage-accent text-sage-bg flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-2">FREE · ANONYMOUS · LOUD</div>
                <div className="text-3xl md:text-4xl font-bold uppercase tracking-tight leading-tight">
                  Whistle<br />Blower
                </div>
              </div>
              <div className="mt-6 text-[10px] uppercase tracking-[0.2em] opacity-90 group-hover:opacity-100">
                Blow it here →
              </div>
            </div>
            <div className="p-6 md:col-span-3 flex flex-col justify-center">
              <p className="text-lg md:text-xl font-bold uppercase tracking-tight mb-3 leading-tight">
                Is that what you are? A whistleblower?
              </p>
              <p className="text-sm md:text-base sage-prose opacity-90 mb-3">
                Blow it here, for free, anonymously. Three AI agents at minimum — and potentially the Founder himself — will review it,
                and we will scream it louder than you ever could. You remain anonymous. <strong>Even we do not know (or care) who files these.</strong>
              </p>
              <p className="text-sm md:text-base sage-prose opacity-90 font-semibold">
                Care to disrupt an industry with the truth? That's exactly what we ARE, at our core.
              </p>
              <div className="mt-4 text-[10px] uppercase tracking-[0.2em] opacity-70">
                {whistleblower.attribution || "Baker and Sage, by Resilience Multimedia Studios"}
              </div>
            </div>
          </Link>
        </div>
      )}

      <div className="mt-16 border-t border-sage-border pt-8 text-sm text-sage-muted sage-prose">
        <p>
          The paid product line lives on <Link to="/products" className="text-sage-accent underline">/products</Link>.
          Every paid dollar we take routes some fraction back to the free wall — that is the loop, and it is the whole point.
        </p>
      </div>
    </>
  );
}

// ── FAQ Ask Box · shared shape with /products · Founder ruling 2026-08-11 ────
function FreeWallFAQAskBox() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState(false);

  const ask = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    setAnswer(null);
    try {
      const r = await fetch(`${API}/api/sage/faq/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setAnswer(d.answer);
        setCached(!!d.cached);
      } else {
        setAnswer("Sage couldn't answer that one. Try rephrasing?");
      }
    } catch {
      setAnswer("Network hiccup. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") ask(); };

  return (
    <div className="mb-10 border border-sage-accent/40 bg-sage-pale/20 p-5" data-testid="free-wall-faq-ask-box">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">
        Ask Sage · free · anonymous · answers cached for zero cost
      </div>
      <div className="flex gap-2">
        <input
          data-testid="free-wall-faq-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Which free doors help me most? · Is this really free? · Who pays for it?"
          maxLength={500}
          className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
        />
        <button
          onClick={ask}
          disabled={busy || q.trim().length < 3}
          data-testid="free-wall-faq-submit"
          className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
        >
          {busy ? "…" : "Ask"}
        </button>
      </div>
      {answer && (
        <div className="mt-4 border-t border-sage-border pt-4" data-testid="free-wall-faq-answer">
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Sage says</div>
            {cached && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent" data-testid="free-wall-faq-cached-badge">
                Served from cache · 0 tokens burned
              </div>
            )}
          </div>
          <div className="text-sm text-sage-text sage-prose whitespace-pre-wrap">{answer}</div>
        </div>
      )}
    </div>
  );
}

function FreeCard({ item }) {
  return (
    <Link
      to={item.route || "/"}
      data-testid={`free-item-${item.key}`}
      className="block border border-sage-border p-6 hover:border-sage-accent transition-colors duration-150 bg-sage-pale/30"
    >
      <div className="flex justify-between items-start mb-3 gap-3">
        <div className="text-2xl font-bold uppercase tracking-tight text-sage-text">{item.name}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] border border-sage-accent text-sage-accent px-2 py-0.5 shrink-0">
          FREE
        </div>
      </div>
      <p className="text-sm text-sage-muted sage-prose mb-4">{item.tagline}</p>
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent">Enter →</div>
    </Link>
  );
}
